import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from '../../contexts/AuthContext';
import activeAgentAiService from '../../services/activeAgentAiService';
import travelieTheme from '../../styles/travelieTheme';

const CONCIERGE_APP_URL = process.env.REACT_APP_CONCIERGE_APP_URL || 'https://voyager.tpcrm.io';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const TIERS = {
  basic: {
    name: 'Basic',
    price: 5,
    tagline: 'Up to 25 AI Leads per month',
    features: [
      'Up to 25 AI-qualified leads per month',
      'Full trip detail extraction (quote-ready leads)',
      'AI Travel Concierge on your public booking link',
      'Automatic CRM lead capture'
    ]
  },
  pro: {
    name: 'Pro',
    price: 10,
    tagline: 'Unlimited AI Leads & Website Embedding',
    features: [
      'Unlimited AI-qualified leads',
      'Custom Website Integration (Embed widget)',
      'Everything in Basic'
    ]
  }
};

const CheckoutForm = ({ tier, onSuccess, onCancel, setParentError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cardholderName, setCardholderName] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    if (!cardholderName.trim()) {
      setError('Cardholder name is required');
      setParentError('Cardholder name is required');
      return;
    }

    setLoading(true);
    setError('');
    setParentError(null);

    const cardElement = elements.getElement(CardElement);

    try {
      const { error: paymentError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: { name: cardholderName }
      });

      if (paymentError) {
        setError(paymentError.message);
        setParentError(paymentError.message);
        setLoading(false);
        return;
      }

      const result = await activeAgentAiService.subscribe(paymentMethod.id, tier);

      if (result.success) {
        onSuccess(tier);
      } else {
        const errorMsg = result.error || 'Subscription failed';
        setError(errorMsg);
        setParentError(errorMsg);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'An error occurred during subscription';
      setError(errorMsg);
      setParentError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: travelieTheme.spacing.lg }}>
      <div style={{ marginBottom: travelieTheme.spacing.md }}>
        <label style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.secondary, display: 'block', marginBottom: travelieTheme.spacing.xs }}>
          Cardholder Name
        </label>
        <input
          type="text"
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value)}
          placeholder="Jane Doe"
          style={{
            width: '100%',
            borderRadius: travelieTheme.components.input.borderRadius,
            border: travelieTheme.components.input.border,
            padding: travelieTheme.components.input.padding,
            fontSize: travelieTheme.components.input.fontSize,
            boxSizing: 'border-box'
          }}
        />
      </div>

      <div style={{ marginBottom: travelieTheme.spacing.md }}>
        <label style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.secondary, display: 'block', marginBottom: travelieTheme.spacing.xs }}>
          Card Details
        </label>
        <div style={{
          border: travelieTheme.components.input.border,
          borderRadius: travelieTheme.components.input.borderRadius,
          padding: travelieTheme.spacing.md
        }}>
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '15px',
                  color: travelieTheme.colors.text.primary,
                  '::placeholder': { color: travelieTheme.colors.text.muted }
                },
                invalid: { color: travelieTheme.colors.status.error }
              }
            }}
          />
        </div>
      </div>

      {error && (
        <div style={{
          background: '#FEE2E2',
          border: '1px solid #FCA5A5',
          color: '#991B1B',
          padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.md}`,
          borderRadius: travelieTheme.components.card.borderRadius,
          fontSize: travelieTheme.typography.fontSize.sm,
          marginBottom: travelieTheme.spacing.md
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: travelieTheme.spacing.md }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          style={{
            flex: 1,
            background: travelieTheme.colors.neutral.card,
            color: travelieTheme.colors.text.secondary,
            border: `1px solid ${travelieTheme.colors.neutral.border}`,
            borderRadius: travelieTheme.components.button.primary.borderRadius,
            height: travelieTheme.components.button.primary.height,
            fontSize: travelieTheme.components.button.primary.fontSize,
            fontWeight: travelieTheme.components.button.primary.fontWeight,
            cursor: 'pointer'
          }}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || loading}
          style={{
            flex: 1,
            background: tier === 'pro' ? travelieTheme.colors.statIcons.purple : travelieTheme.components.button.primary.background,
            color: travelieTheme.components.button.primary.color,
            borderRadius: travelieTheme.components.button.primary.borderRadius,
            height: travelieTheme.components.button.primary.height,
            fontSize: travelieTheme.components.button.primary.fontSize,
            fontWeight: travelieTheme.components.button.primary.fontWeight,
            border: 'none',
            cursor: loading ? 'default' : 'pointer',
            opacity: loading ? 0.7 : 1
          }}
        >
          {loading ? 'Processing...' : `Subscribe - $${TIERS[tier].price}/month`}
        </button>
      </div>
    </form>
  );
};

const PricingCard = ({ tierKey, isCurrentTier, onSelect, popular }) => {
  const tier = TIERS[tierKey];

  return (
    <div style={{
      position: 'relative',
      background: travelieTheme.colors.neutral.card,
      borderRadius: '16px',
      padding: travelieTheme.spacing['2xl'],
      border: popular ? `2px solid ${travelieTheme.colors.statIcons.purple}` : `1px solid ${travelieTheme.colors.neutral.border}`,
      boxShadow: popular ? '0 12px 24px rgba(139, 92, 246, 0.15)' : travelieTheme.components.card.shadow,
      transform: popular ? 'scale(1.02)' : 'none',
      flex: 1,
      minWidth: '280px'
    }}>
      {popular && (
        <div style={{
          position: 'absolute',
          top: '-14px',
          right: travelieTheme.spacing.xl,
          background: travelieTheme.colors.statIcons.purple,
          color: '#fff',
          fontSize: travelieTheme.typography.fontSize.xs,
          fontWeight: travelieTheme.typography.fontWeight.semibold,
          padding: `4px ${travelieTheme.spacing.md}`,
          borderRadius: '999px'
        }}>
          Best Value
        </div>
      )}

      <h3 style={{
        fontSize: travelieTheme.typography.fontSize.lg,
        fontWeight: travelieTheme.typography.fontWeight.semibold,
        color: travelieTheme.colors.text.primary,
        margin: 0
      }}>
        {tier.name}
      </h3>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', margin: `${travelieTheme.spacing.sm} 0` }}>
        <span style={{ fontSize: travelieTheme.typography.fontSize['4xl'], fontWeight: travelieTheme.typography.fontWeight.bold, color: travelieTheme.colors.text.primary }}>
          ${tier.price}
        </span>
        <span style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.secondary }}>/month</span>
      </div>

      <p style={{
        fontSize: travelieTheme.typography.fontSize.sm,
        fontWeight: travelieTheme.typography.fontWeight.medium,
        color: popular ? travelieTheme.colors.statIcons.purple : travelieTheme.colors.primary.main,
        marginBottom: travelieTheme.spacing.lg
      }}>
        {tier.tagline}
      </p>

      <ul style={{ listStyle: 'none', padding: 0, margin: `0 0 ${travelieTheme.spacing['2xl']} 0` }}>
        {tier.features.map((feature) => (
          <li key={feature} style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: travelieTheme.spacing.sm,
            marginBottom: travelieTheme.spacing.sm,
            fontSize: travelieTheme.typography.fontSize.sm,
            color: travelieTheme.colors.text.secondary
          }}>
            <span style={{ color: travelieTheme.colors.status.success, marginTop: '1px' }}>✓</span>
            {feature}
          </li>
        ))}
      </ul>

      <button
        onClick={() => onSelect(tierKey)}
        disabled={isCurrentTier}
        style={{
          width: '100%',
          background: isCurrentTier ? travelieTheme.colors.neutral.hover : (popular ? travelieTheme.colors.statIcons.purple : travelieTheme.components.button.primary.background),
          color: isCurrentTier ? travelieTheme.colors.text.muted : '#fff',
          borderRadius: travelieTheme.components.button.primary.borderRadius,
          height: travelieTheme.components.button.primary.height,
          fontSize: travelieTheme.components.button.primary.fontSize,
          fontWeight: travelieTheme.components.button.primary.fontWeight,
          border: 'none',
          cursor: isCurrentTier ? 'default' : 'pointer'
        }}
      >
        {isCurrentTier ? 'Current Plan' : `Subscribe to ${tier.name}`}
      </button>
    </div>
  );
};

const PublicLinkSection = ({ agentId }) => {
  const [copied, setCopied] = useState(false);
  const publicLink = `${CONCIERGE_APP_URL}/?agent=${agentId}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(publicLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy public link:', err);
    }
  };

  return (
    <div style={{
      background: travelieTheme.colors.neutral.card,
      border: `1px solid ${travelieTheme.colors.neutral.border}`,
      borderRadius: travelieTheme.components.card.borderRadius,
      padding: travelieTheme.spacing.lg,
      marginBottom: travelieTheme.spacing['2xl']
    }}>
      <h3 style={{
        fontSize: travelieTheme.typography.fontSize.base,
        fontWeight: travelieTheme.typography.fontWeight.semibold,
        color: travelieTheme.colors.text.primary,
        margin: 0,
        marginBottom: travelieTheme.spacing.xs
      }}>
        Your Public Link
      </h3>
      <p style={{
        fontSize: travelieTheme.typography.fontSize.sm,
        color: travelieTheme.colors.text.secondary,
        margin: 0,
        marginBottom: travelieTheme.spacing.md
      }}>
        Share this link anywhere (your website, email signature, social bio) to put your AI Travel Concierge to work - every lead it captures routes straight into your CRM pipeline. Available on every plan.
      </p>
      <div style={{ display: 'flex', gap: travelieTheme.spacing.sm, flexWrap: 'wrap' }}>
        <input
          type="text"
          readOnly
          value={publicLink}
          onFocus={(e) => e.target.select()}
          style={{
            flex: 1,
            minWidth: '240px',
            borderRadius: travelieTheme.components.input.borderRadius,
            border: travelieTheme.components.input.border,
            padding: travelieTheme.components.input.padding,
            fontSize: travelieTheme.components.input.fontSize,
            color: travelieTheme.colors.text.secondary,
            background: travelieTheme.colors.neutral.background
          }}
        />
        <button
          onClick={handleCopy}
          style={{
            background: copied ? travelieTheme.colors.status.success : travelieTheme.components.button.primary.background,
            color: '#fff',
            borderRadius: travelieTheme.components.button.primary.borderRadius,
            padding: `0 ${travelieTheme.spacing.xl}`,
            height: travelieTheme.components.button.primary.height,
            fontSize: travelieTheme.components.button.primary.fontSize,
            fontWeight: travelieTheme.components.button.primary.fontWeight,
            border: 'none',
            cursor: 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          {copied ? 'Copied!' : 'Copy Link'}
        </button>
      </div>
    </div>
  );
};

const buildEmbedSnippet = (agentId) =>
  `<iframe
  src="${CONCIERGE_APP_URL}/?agent=${agentId}"
  width="100%"
  height="600px"
  style="border:none; border-radius:12px; box-shadow: 0 4px 12px rgba(0,0,0,0.1);"
  title="AI Travel Concierge"
></iframe>`;

const WebsiteIntegrationSection = ({ agentId, isPro, onUpgradeClick }) => {
  const [copied, setCopied] = useState(false);
  const snippet = buildEmbedSnippet(agentId);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy embed code:', err);
    }
  };

  return (
    <div style={{
      background: travelieTheme.colors.neutral.card,
      border: `1px solid ${travelieTheme.colors.neutral.border}`,
      borderRadius: travelieTheme.components.card.borderRadius,
      padding: travelieTheme.spacing.lg,
      marginBottom: travelieTheme.spacing['2xl']
    }}>
      <h3 style={{
        fontSize: travelieTheme.typography.fontSize.base,
        fontWeight: travelieTheme.typography.fontWeight.semibold,
        color: travelieTheme.colors.text.primary,
        margin: 0,
        marginBottom: travelieTheme.spacing.xs,
        display: 'flex',
        alignItems: 'center',
        gap: travelieTheme.spacing.sm
      }}>
        Website Integration (Pro Feature)
        <span style={{
          background: travelieTheme.colors.statIcons.purple,
          color: '#fff',
          fontSize: travelieTheme.typography.fontSize.xs,
          fontWeight: travelieTheme.typography.fontWeight.semibold,
          padding: `2px ${travelieTheme.spacing.sm}`,
          borderRadius: '999px'
        }}>
          PRO
        </span>
      </h3>
      <p style={{
        fontSize: travelieTheme.typography.fontSize.sm,
        color: travelieTheme.colors.text.secondary,
        margin: 0,
        marginBottom: travelieTheme.spacing.md
      }}>
        Embed your AI Travel Concierge directly on your own website with a copy-paste snippet - travelers chat with it right on your page, and every lead still routes straight into your CRM pipeline.
      </p>

      <div style={{ position: 'relative' }}>
        <div style={{
          filter: isPro ? 'none' : 'blur(6px)',
          userSelect: isPro ? 'auto' : 'none',
          pointerEvents: isPro ? 'auto' : 'none'
        }}>
          <pre style={{
            margin: 0,
            marginBottom: travelieTheme.spacing.md,
            background: travelieTheme.colors.neutral.background,
            border: travelieTheme.components.input.border,
            borderRadius: travelieTheme.components.input.borderRadius,
            padding: travelieTheme.spacing.md,
            fontSize: travelieTheme.typography.fontSize.sm,
            color: travelieTheme.colors.text.primary,
            fontFamily: 'Menlo, Consolas, monospace',
            overflowX: 'auto',
            whiteSpace: 'pre'
          }}>
            {snippet}
          </pre>
          <button
            onClick={handleCopy}
            disabled={!isPro}
            style={{
              background: copied ? travelieTheme.colors.status.success : travelieTheme.components.button.primary.background,
              color: '#fff',
              borderRadius: travelieTheme.components.button.primary.borderRadius,
              padding: `0 ${travelieTheme.spacing.xl}`,
              height: travelieTheme.components.button.primary.height,
              fontSize: travelieTheme.components.button.primary.fontSize,
              fontWeight: travelieTheme.components.button.primary.fontWeight,
              border: 'none',
              cursor: isPro ? 'pointer' : 'default'
            }}
          >
            {copied ? 'Copied!' : 'Copy to Clipboard'}
          </button>
        </div>

        {!isPro && (
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255, 255, 255, 0.35)',
            borderRadius: travelieTheme.components.card.borderRadius
          }}>
            <button
              onClick={onUpgradeClick}
              style={{
                background: travelieTheme.colors.statIcons.purple,
                color: '#fff',
                borderRadius: travelieTheme.components.button.primary.borderRadius,
                padding: `${travelieTheme.spacing.md} ${travelieTheme.spacing.xl}`,
                fontSize: travelieTheme.components.button.primary.fontSize,
                fontWeight: travelieTheme.components.button.primary.fontWeight,
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 8px 20px rgba(139, 92, 246, 0.35)'
              }}
            >
              Upgrade to Pro to unlock custom website embedding!
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const formatDate = (isoString) => {
  if (!isoString) return null;
  return new Date(isoString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const CancelConfirmModal = ({ canceling, onConfirm, onClose }) => (
  <div style={{
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: travelieTheme.spacing.lg
  }}>
    <div style={{
      background: travelieTheme.colors.neutral.card,
      borderRadius: '16px',
      padding: travelieTheme.spacing['2xl'],
      maxWidth: '420px',
      width: '100%',
      boxShadow: travelieTheme.components.card.shadow
    }}>
      <h3 style={{ margin: 0, marginBottom: travelieTheme.spacing.sm, color: travelieTheme.colors.text.primary }}>
        Cancel Voyager AI?
      </h3>
      <p style={{
        margin: 0,
        marginBottom: travelieTheme.spacing.lg,
        color: travelieTheme.colors.text.secondary,
        fontSize: travelieTheme.typography.fontSize.sm
      }}>
        Your subscription won't renew, but you'll keep full access to your AI Travel Concierge, public link, and website embed until the end of your current billing period. You can resubscribe any time.
      </p>
      <div style={{ display: 'flex', gap: travelieTheme.spacing.md }}>
        <button
          type="button"
          onClick={onClose}
          disabled={canceling}
          style={{
            flex: 1,
            background: travelieTheme.colors.neutral.card,
            color: travelieTheme.colors.text.secondary,
            border: `1px solid ${travelieTheme.colors.neutral.border}`,
            borderRadius: travelieTheme.components.button.primary.borderRadius,
            height: travelieTheme.components.button.primary.height,
            fontSize: travelieTheme.components.button.primary.fontSize,
            fontWeight: travelieTheme.components.button.primary.fontWeight,
            cursor: 'pointer'
          }}
        >
          Keep My Subscription
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={canceling}
          style={{
            flex: 1,
            background: travelieTheme.colors.status.error,
            color: '#fff',
            borderRadius: travelieTheme.components.button.primary.borderRadius,
            height: travelieTheme.components.button.primary.height,
            fontSize: travelieTheme.components.button.primary.fontSize,
            fontWeight: travelieTheme.components.button.primary.fontWeight,
            border: 'none',
            cursor: canceling ? 'default' : 'pointer',
            opacity: canceling ? 0.7 : 1
          }}
        >
          {canceling ? 'Canceling...' : 'Yes, Cancel'}
        </button>
      </div>
    </div>
  </div>
);

const ManageSubscriptionCard = ({ status, onUpgradeToPro, upgrading, onCancelClick }) => {
  const nextBillingLabel = formatDate(status.nextBillingDate);

  return (
    <div style={{
      background: travelieTheme.colors.neutral.card,
      border: `1px solid ${travelieTheme.colors.neutral.border}`,
      borderRadius: travelieTheme.components.card.borderRadius,
      padding: travelieTheme.spacing.lg
    }}>
      <h3 style={{
        fontSize: travelieTheme.typography.fontSize.base,
        fontWeight: travelieTheme.typography.fontWeight.semibold,
        color: travelieTheme.colors.text.primary,
        margin: 0,
        marginBottom: travelieTheme.spacing.md
      }}>
        Manage Subscription
      </h3>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: travelieTheme.spacing.lg,
        marginBottom: travelieTheme.spacing.lg
      }}>
        <div>
          <div style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.secondary }}>
            Current Plan
          </div>
          <strong style={{ color: travelieTheme.colors.text.primary, fontSize: travelieTheme.typography.fontSize.lg }}>
            {TIERS[status.tier]?.name || status.tier} - ${TIERS[status.tier]?.price}/month
          </strong>
          <div style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.secondary, marginTop: travelieTheme.spacing.xs }}>
            {status.unlimited
              ? 'Unlimited AI leads this month'
              : `${status.leadsThisMonth} / ${status.leadCap} AI leads used this month`}
          </div>
        </div>

        <div>
          <div style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.secondary }}>
            {status.cancelAtPeriodEnd ? 'Access Ends' : 'Next Billing Date'}
          </div>
          <strong style={{ color: travelieTheme.colors.text.primary, fontSize: travelieTheme.typography.fontSize.lg }}>
            {nextBillingLabel || '—'}
          </strong>
          {status.cancelAtPeriodEnd && (
            <div style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.status.error, marginTop: travelieTheme.spacing.xs }}>
              Your plan is set to cancel and won't renew.
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: travelieTheme.spacing.md, flexWrap: 'wrap' }}>
        {status.tier === 'basic' && (
          <button
            type="button"
            onClick={onUpgradeToPro}
            disabled={upgrading}
            style={{
              background: travelieTheme.colors.statIcons.purple,
              color: '#fff',
              borderRadius: travelieTheme.components.button.primary.borderRadius,
              padding: `0 ${travelieTheme.spacing.xl}`,
              height: travelieTheme.components.button.primary.height,
              fontSize: travelieTheme.components.button.primary.fontSize,
              fontWeight: travelieTheme.components.button.primary.fontWeight,
              border: 'none',
              cursor: upgrading ? 'default' : 'pointer',
              opacity: upgrading ? 0.7 : 1
            }}
          >
            {upgrading ? 'Upgrading...' : 'Upgrade to Pro'}
          </button>
        )}
        {!status.cancelAtPeriodEnd && (
          <button
            type="button"
            onClick={onCancelClick}
            style={{
              background: 'transparent',
              color: travelieTheme.colors.status.error,
              border: `1px solid ${travelieTheme.colors.status.error}`,
              borderRadius: travelieTheme.components.button.primary.borderRadius,
              padding: `0 ${travelieTheme.spacing.xl}`,
              height: travelieTheme.components.button.primary.height,
              fontSize: travelieTheme.components.button.primary.fontSize,
              fontWeight: travelieTheme.components.button.primary.fontWeight,
              cursor: 'pointer'
            }}
          >
            Cancel Subscription
          </button>
        )}
      </div>
    </div>
  );
};

const SCENARIOS = [
  { emoji: '🚢', title: 'Cruises', description: 'Disney Cruise Line, Royal Caribbean, Carnival, or "no preference" - dates, destination, party size, and stateroom category.' },
  { emoji: '🎢', title: 'Theme Parks', description: 'Walt Disney World, Disneyland, Universal Orlando/Hollywood - travel dates, ticket type, and park days.' },
  { emoji: '✈️', title: 'Flights', description: 'Departure/arrival airports, travel dates, and party size.' },
  { emoji: '🏨', title: 'Hotels', description: 'Destination, check-in/check-out dates, and room preferences.' },
  { emoji: '🚗', title: 'Car Rentals', description: 'Pick-up/drop-off locations and dates.' },
  { emoji: '🌎', title: 'Everything Else', description: 'Any other trip - the AI asks smart follow-up questions to qualify it.' }
];

const HowItWorksSection = () => (
  <div style={{ marginBottom: travelieTheme.spacing['2xl'] }}>
    <div style={{
      background: travelieTheme.colors.neutral.card,
      border: `1px solid ${travelieTheme.colors.neutral.border}`,
      borderRadius: travelieTheme.components.card.borderRadius,
      padding: travelieTheme.spacing.lg,
      marginBottom: travelieTheme.spacing.lg
    }}>
      <h3 style={{
        fontSize: travelieTheme.typography.fontSize.base,
        fontWeight: travelieTheme.typography.fontWeight.semibold,
        color: travelieTheme.colors.text.primary,
        margin: 0,
        marginBottom: travelieTheme.spacing.sm
      }}>
        How It Works
      </h3>
      <p style={{
        fontSize: travelieTheme.typography.fontSize.sm,
        color: travelieTheme.colors.text.secondary,
        margin: 0,
        marginBottom: travelieTheme.spacing.lg
      }}>
        Your AI Travel Concierge is a conversational assistant that lives on its own link (and, on Pro, embeds directly on your website). It greets travelers, asks the right questions for whatever kind of trip they're planning, and hands you a fully-qualified lead in your CRM - no forms, no back-and-forth, even while you're offline.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: travelieTheme.spacing.md }}>
        {SCENARIOS.map((scenario) => (
          <div key={scenario.title} style={{
            background: travelieTheme.colors.neutral.background,
            borderRadius: travelieTheme.components.input.borderRadius,
            padding: travelieTheme.spacing.md
          }}>
            <div style={{ fontSize: '28px', marginBottom: travelieTheme.spacing.xs }}>{scenario.emoji}</div>
            <strong style={{ display: 'block', color: travelieTheme.colors.text.primary, fontSize: travelieTheme.typography.fontSize.sm, marginBottom: travelieTheme.spacing.xs }}>
              {scenario.title}
            </strong>
            <p style={{ margin: 0, fontSize: travelieTheme.typography.fontSize.xs, color: travelieTheme.colors.text.secondary }}>
              {scenario.description}
            </p>
          </div>
        ))}
      </div>
    </div>

    <div style={{ display: 'flex', gap: travelieTheme.spacing.lg, flexWrap: 'wrap', justifyContent: 'center' }}>
      <img
        src="/images/active-agent-ai/concierge-welcome.png"
        alt="AI Travel Concierge chat - greeting a traveler"
        style={{
          width: '260px',
          maxWidth: '100%',
          borderRadius: '12px',
          border: `1px solid ${travelieTheme.colors.neutral.border}`,
          boxShadow: travelieTheme.components.card.shadow
        }}
      />
      <img
        src="/images/active-agent-ai/concierge-conversation.png"
        alt="AI Travel Concierge chat - qualifying a trip"
        style={{
          width: '260px',
          maxWidth: '100%',
          borderRadius: '12px',
          border: `1px solid ${travelieTheme.colors.neutral.border}`,
          boxShadow: travelieTheme.components.card.shadow
        }}
      />
    </div>
  </div>
);

const ActiveAgentAI = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutTier, setCheckoutTier] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [tierChangeLoading, setTierChangeLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [canceling, setCanceling] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const data = await activeAgentAiService.getStatus();
      setStatus(data);
    } catch (err) {
      console.error('Error loading ActiveAgent AI status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTier = (tierKey) => {
    setError(null);
    setCheckoutTier(tierKey);
  };

  const handleCheckoutSuccess = (tierKey) => {
    setCheckoutTier(null);
    setSuccessMessage(`You're subscribed to the ${TIERS[tierKey].name} plan!`);
    loadStatus();
  };

  // Basic -> Pro on an already-active subscription reuses the payment
  // method already on file (see /change-tier), so no card re-entry needed.
  const handleUpgradeToPro = async () => {
    setError(null);
    setSuccessMessage(null);
    setTierChangeLoading(true);
    try {
      await activeAgentAiService.changeTier('pro');
      setSuccessMessage("You're now on the Pro plan!");
      await loadStatus();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to upgrade plan');
    } finally {
      setTierChangeLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setError(null);
    setCanceling(true);
    try {
      const result = await activeAgentAiService.cancelSubscription();
      setShowCancelConfirm(false);
      setSuccessMessage(result?.message || "Your subscription won't renew.");
      await loadStatus();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to cancel subscription');
    } finally {
      setCanceling(false);
    }
  };

  // The blurred embed-teaser's upgrade CTA needs different behavior
  // depending on whether the agent already has a card on file: unsubscribed
  // agents go through full checkout, Basic subscribers just switch tiers.
  const handleWebsiteIntegrationUpgradeClick = () => {
    if (status?.active) {
      handleUpgradeToPro();
    } else {
      handleSelectTier('pro');
    }
  };

  const currentTier = status?.active ? status.tier : null;
  const isSubscribed = Boolean(status?.active);

  if (loading) {
    return (
      <div style={{ padding: travelieTheme.spacing.lg, textAlign: 'center', color: travelieTheme.colors.text.secondary }}>
        Loading Voyager AI...
      </div>
    );
  }

  return (
    <div style={{ padding: travelieTheme.spacing.lg, maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: travelieTheme.spacing['3xl'] }}>
        <h2 style={{
          fontSize: travelieTheme.typography.fontSize['2xl'],
          fontWeight: travelieTheme.typography.fontWeight.semibold,
          color: travelieTheme.colors.text.primary,
          margin: 0
        }}>
          Voyager AI
        </h2>
        <p style={{
          fontSize: travelieTheme.typography.fontSize.lg,
          color: travelieTheme.colors.text.secondary,
          marginTop: travelieTheme.spacing.sm,
          maxWidth: '640px',
          marginLeft: 'auto',
          marginRight: 'auto'
        }}>
          Capture leads while you sleep with your intelligent travel assistant.
        </p>
      </div>

      {successMessage && (
        <div style={{
          background: travelieTheme.colors.status.successLight,
          color: '#065F46',
          padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.md}`,
          borderRadius: travelieTheme.components.card.borderRadius,
          marginBottom: travelieTheme.spacing.lg,
          textAlign: 'center',
          fontSize: travelieTheme.typography.fontSize.sm
        }}>
          {successMessage}
        </div>
      )}

      {error && (
        <div style={{
          background: '#FEE2E2',
          border: '1px solid #FCA5A5',
          color: '#991B1B',
          padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.md}`,
          borderRadius: travelieTheme.components.card.borderRadius,
          marginBottom: travelieTheme.spacing.lg,
          fontSize: travelieTheme.typography.fontSize.sm
        }}>
          {error}
        </div>
      )}

      {isSubscribed ? (
        // --- State B: Management View --------------------------------
        <>
          <div style={{
            background: travelieTheme.colors.primary.light,
            borderRadius: travelieTheme.components.card.borderRadius,
            padding: travelieTheme.spacing.lg,
            marginBottom: travelieTheme.spacing['2xl'],
            fontSize: travelieTheme.typography.fontSize.sm,
            color: travelieTheme.colors.text.primary
          }}>
            <strong>Your AI is ready!</strong> Share your link or embed the widget below to start capturing leads while you sleep.
          </div>

          {user?.id && <PublicLinkSection agentId={user.id} />}

          {user?.id && (
            <WebsiteIntegrationSection
              agentId={user.id}
              isPro={currentTier === 'pro'}
              onUpgradeClick={handleWebsiteIntegrationUpgradeClick}
            />
          )}

          <ManageSubscriptionCard
            status={status}
            onUpgradeToPro={handleUpgradeToPro}
            upgrading={tierChangeLoading}
            onCancelClick={() => setShowCancelConfirm(true)}
          />

          {showCancelConfirm && (
            <CancelConfirmModal
              canceling={canceling}
              onConfirm={handleCancelSubscription}
              onClose={() => setShowCancelConfirm(false)}
            />
          )}
        </>
      ) : (
        // --- State A: Unsubscribed - education + pricing cards --------
        // No public link or website embed here - an unsubscribed agent
        // has nothing live to share yet, so showing those would just be
        // confusing (or worse, get shared before they've paid for it).
        <>
          <HowItWorksSection />

          {checkoutTier ? (
            <div style={{
              maxWidth: '480px',
              margin: '0 auto',
              background: travelieTheme.colors.neutral.card,
              borderRadius: '16px',
              padding: travelieTheme.spacing['2xl'],
              border: `1px solid ${travelieTheme.colors.neutral.border}`,
              boxShadow: travelieTheme.components.card.shadow
            }}>
              <h3 style={{ margin: 0, color: travelieTheme.colors.text.primary }}>
                Subscribe to {TIERS[checkoutTier].name} - ${TIERS[checkoutTier].price}/month
              </h3>
              <Elements stripe={stripePromise}>
                <CheckoutForm
                  tier={checkoutTier}
                  onSuccess={handleCheckoutSuccess}
                  onCancel={() => setCheckoutTier(null)}
                  setParentError={setError}
                />
              </Elements>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: travelieTheme.spacing['2xl'], flexWrap: 'wrap', justifyContent: 'center' }}>
              <PricingCard tierKey="basic" isCurrentTier={false} onSelect={handleSelectTier} popular={false} />
              <PricingCard tierKey="pro" isCurrentTier={false} onSelect={handleSelectTier} popular />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ActiveAgentAI;
